import { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Autocomplete,
  Option,
} from '@beeline/design-system-react/extracted-styles';

import { useFloorMap } from 'src/components/FloorMap/FloorMapProvider';
import { getParkingState } from 'src/store/parking/selectors';
import { openParking } from 'src/store/parking/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { PlaceType } from 'src/types';

const renderValue = (option: Option<string>): string => option.value ?? '';

export interface ParkingSearchProps {
  onOpen?: () => void;
}

export const ParkingSearch: FC<ParkingSearchProps> = ({ onOpen }) => {
  const dispatch = useAppDispatch();

  const { bookingObjects } = useSelector(getParkingState);
  const { scrollTo } = useFloorMap();

  const options = useMemo((): Option<string>[] => {
    const result: Option<string>[] = [];

    for (const [key, value] of bookingObjects) {
      if (!key || !value.title) continue;

      result.push({ id: key, value: value.title });
    }

    return result.sort((a, b) => a.value.localeCompare(b.value));
  }, [bookingObjects]);

  const [value, setValue] = useState<Option<string> | null>(null);
  const [input, setInput] = useState('');

  const filteredOptions = useMemo((): Option<string>[] => {
    const search = input.toLowerCase();

    return options.filter(option =>
      option.value.toLowerCase().includes(search),
    );
  }, [input, options]);

  const handleChange = useCallback(
    async (option: Option<string>) => {
      setValue(option);

      const found = bookingObjects.get(option.id as string);
      if (found && found.type === PlaceType.Parking) {
        onOpen?.();

        dispatch(openParking(found.mapPlaceId));

        scrollTo(found);
      }
    },
    [bookingObjects, dispatch, onOpen, scrollTo],
  );

  const handleClear = useCallback(async () => {
    setInput('');
    setValue(null);
  }, []);

  return (
    <Autocomplete
      placeholder='Место'
      type='search'
      size='medium'
      disabled={!options.length}
      options={filteredOptions}
      value={value}
      noOptionsText='места не найдены'
      onChange={handleChange}
      renderValue={renderValue}
      onInputChange={setInput}
      onInputClear={handleClear}
      loading={!options.length}
      fullWidth
    />
  );
};
